import React, { useEffect, useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler } from "react-hook-form";
import axios from "axios";
import Cookies from "js-cookie";

import Button from "../../components/Button";
import AppLogo from "../../components/AppLogo/AppLogo";
import FormItem from "../../components/FormItem";
import InputErrorMessage from "../../components/InputErrorMessage";
import InputField from "../../components/InputField";
import ChangePasswordSkeleton from "../Skeletons/ChangePasswordSkeleton";

import { AuthContext } from "../../contexts/AuthContext";
import { generateRequestConfig } from "../../constants";
import useTimer from "../../hooks/useTimer";
import { getAppInfo, handleErrorForms, getIdApp, getDeviceId, ensureCsrfToken } from "../../utils";
import {
  changePasswordFields,
  getPasswordErrorMessages,
  ChangePasswordFormDataType,
} from "./ChangePassword.constants";

type ChangePasswordState = {
  generalErrors?: string;
  appInfo: any;
};

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const { handleLogIn, AuthContextState } = useContext(AuthContext);
  const { csrf_token, idApp } = AuthContextState;

  const [state, setState] = useState<ChangePasswordState>({
    appInfo: null,
    generalErrors: undefined,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ChangePasswordFormDataType>();
  useEffect(() => {

    const resetToken = Cookies.get("x-reset-password-token");
    if (!resetToken) {
      navigate(`/${currentLang}/login/?app_id=${idApp}`);
      return;
    }

    // Check the token expiration if it's a JWT
    try {
      const payload = JSON.parse(atob(resetToken.split('.')[1]));
      if (payload.exp && Date.now() / 1000 > payload.exp) {
        Cookies.remove("x-reset-password-token");
        navigate(`/${currentLang}/forgot/?app_id=${idApp}`);
      }
    } catch (e) {
      // If the token is invalid, remove it and redirect
      Cookies.remove("x-reset-password-token");
      navigate(`/${currentLang}/forgot/?app_id=${idApp}`);
    }
    
  }, [navigate, currentLang, idApp]);
  const isLoaded = useTimer(400);

  useEffect(() => {
    document.title = t("Reset Password");

    if (!getIdApp()) navigate("/");

    if (!state.appInfo) {
      getAppInfo((data) =>
        setState((prev) => ({ ...prev, appInfo: data }))
      );
    }
  }, [t, navigate, state.appInfo]);

  const handleSubmitChangePassword: SubmitHandler<ChangePasswordFormDataType> = async (data) => {
    const csrfToken = await ensureCsrfToken(csrf_token);
    const requestConfig = generateRequestConfig(
      "put",
      "resetpassword",
      {
        deviceId: getDeviceId(),
        appId: idApp,
        ...data,
      },
      {
        "X-RESET-TOKEN": `Bearer ${Cookies.get("x-reset-password-token")}`,
        "X-CSRF-TOKEN": csrfToken,
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Accept-Language": currentLang,
      }
    );

    setState((prev) => ({
      ...prev,
      generalErrors: undefined,
    }));

    try {
      const res = await axios(requestConfig);
      if (res.status === 200 && res.data.message !== undefined) {
        Cookies.remove("x-reset-password-token");
        handleLogIn(res.data);
        //navigate(`/${currentLang}/login/?app_id=${idApp}`);
      }
    } catch (error) {
      const errorAction = handleErrorForms(error);
      if (errorAction) {
        const { message, field } = errorAction;
        if (field) {
          setError(field as "password" | "passwordRepeat", {
            type: "server",
            message,
          });
        } else {
          setState((prev) => ({ ...prev, generalErrors: message }));
        }
      }
    }
  };

  const passwordErrorMessages = getPasswordErrorMessages(errors);
  const { appInfo, generalErrors } = state;
  const {
    password: passwordField,
    passwordRepeat: passwordRepeatField,
  } = changePasswordFields;

  return (
    <>
      {!isLoaded ? (
        <ChangePasswordSkeleton />
      ) : (
        <>
          <h1 className="text-center auth-title-form">
            {t("Change Password")}
            <AppLogo
              src={appInfo?.urlImage}
              name={appInfo?.name}
              frameClassName="auth-title-logo-frame"
              imageClassName="auth-title-logo"
              placeholderClassName="auth-title-logo-placeholder"
            />
          </h1>

          <div className="card-body">
            <form name="ChangePassword" onSubmit={handleSubmit(handleSubmitChangePassword)}>
              <FormItem label={t(passwordField.label)} labelFor={passwordField.id}>
                <InputField
                  autoFocus
                  id={passwordField.id}
                  type={passwordField.type}
                  placeholder={t(passwordField.placeholder)}
                  iconName={passwordField.iconName}
                  {...register(passwordField.name, passwordField.validation)}
                  className={generalErrors || errors.password ? "error" : undefined}
                />
                {errors.password && (
                  <InputErrorMessage msg={t(passwordErrorMessages[errors.password.type])} />
                )}
              </FormItem>

              <FormItem label={t(passwordRepeatField.label)} labelFor={passwordRepeatField.id}>
                <InputField
                  id={passwordRepeatField.id}
                  type={passwordRepeatField.type}
                  placeholder={t(passwordRepeatField.placeholder)}
                  iconName={passwordRepeatField.iconName}
                  {...register(passwordRepeatField.name, passwordRepeatField.validation)}
                  className={generalErrors || errors.passwordRepeat ? "error" : undefined}
                />
                {errors.passwordRepeat && (
                  <InputErrorMessage msg={t(passwordErrorMessages[errors.passwordRepeat.type])} />
                )}
                {generalErrors && <InputErrorMessage msg={t(generalErrors)} />}
              </FormItem>

              <FormItem>
                <Button
                  type="submit"
                  variant="default"
                  width={100}
                  height={48}
                  text={t("Confirm")}
                />
              </FormItem>

              <div className="p-3 optext pb-5">
                {t("Continuing you agree with our")}{" "}
                <Link to={`/${currentLang}/terms_and_conditions/`}>
                  {t("Terms and Conditions")}
                </Link>{" "}
                {t("and")}{" "}
                <Link to={`/${currentLang}/privacy_policy/`}>
                  {t("Privacy Policy")}
                </Link>
                .
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
};

export default ChangePassword;
